import React, { useState, useEffect } from 'react';
import { Select, Form, Spin } from 'antd';
import type { ComponentConfig, ValueChangeEvent } from '../types';
import { getMetadataDropdownOptions } from '../types';
import { metadataService } from '../../../api/services/metadata';
import type { MetadataType, MetadataItem } from '../../../api/types';
import { createContextLogger } from '../../../utils/logger';

const logger = createContextLogger('MetadataDropdown');

const { Option } = Select;

interface MetadataDropdownProps {
  config: ComponentConfig;
  value?: any;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const MetadataDropdown: React.FC<MetadataDropdownProps> = ({
  config,
  value,
  onChange,
  pageId,
  error
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<MetadataItem[]>([]);

  // Extract metadata dropdown specific options from additionalOptions
  const metadataDropdownOptions = getMetadataDropdownOptions(config);

  useEffect(() => {
    const fetchMetadataOptions = async () => {
      if (!metadataDropdownOptions.metadataType) {
        logger.warn('MetadataDropdown: metadataType is required');
        return;
      }

      setLoading(true);
      try {
        logger.debug('Fetching metadata for type:', metadataDropdownOptions.metadataType);
        
        // Fetch all metadata
        const metadata: MetadataType[] = await metadataService.getMetadata();
        
        // Find the metadata type that matches our metadataType
        const targetMetadataType = metadata.find(type => type.key === metadataDropdownOptions.metadataType);
        
        if (targetMetadataType && targetMetadataType.items) {
          setOptions(targetMetadataType.items);
          logger.debug('Metadata options loaded:', {
            type: metadataDropdownOptions.metadataType,
            count: targetMetadataType.items.length
          });
        } else {
          setOptions([]);
          logger.debug('No items found for metadata type:', metadataDropdownOptions.metadataType);
        }
      } catch (error) {
        logger.error('Failed to fetch metadata options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadataOptions();
  }, [metadataDropdownOptions.metadataType]);

  const handleChange = (selectedValue: any) => {
    onChange({
      componentId: config.id,
      value: selectedValue,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'custom':
          rules.push({
            validator: rule.value,
            message: rule.message
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const getCurrentValue = () => {
    return value !== undefined ? value : config.defaultValue;
  };

  const selectProps = {
    value: getCurrentValue(),
    onChange: handleChange,
    disabled: config.disabled || loading,
    allowClear: metadataDropdownOptions.allowClear !== false,
    showSearch: metadataDropdownOptions.searchable !== false,
    placeholder: loading 
      ? 'Loading...' 
      : metadataDropdownOptions.placeholder || `Select ${config.label.toLowerCase()}`,
    style: { width: '100%' },
    filterOption: metadataDropdownOptions.searchable !== false ? (input: string, option: any) => {
      return option?.children?.toLowerCase().includes(input.toLowerCase()) ||
             option?.value?.toLowerCase().includes(input.toLowerCase());
    } : false,
    notFoundContent: loading ? <Spin size="small" /> : 'No data'
  };

  const formItemProps = {
    label: config.label,
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Form.Item {...formItemProps}>
      <Select {...selectProps}>
        {options.map((item) => (
          <Option 
            key={item.key} 
            value={item.key}
            title={item.description}
          >
            {item.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
}; 